import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import {debounce, last} from "lodash";
import {useRef, RefObject} from "react";
import {cflHeaderLinks} from "data/constants/routing";

export interface ICFLHeaderDesktopController extends ViewController {
	i18n: ILocalizationStore;

	get width(): number;
	get showDropList(): boolean;

	get isAuthorized(): boolean;
	get localeUrls(): typeof cflHeaderLinks[keyof typeof cflHeaderLinks];

	get listRef(): RefObject<HTMLUListElement>;
	get dropMenuRef(): RefObject<HTMLDivElement>;

	initLayout: () => void;
}

@injectable()
export class CFLHeaderDesktopController implements ICFLHeaderDesktopController {
	private _listRef = useRef<HTMLUListElement>(null);
	private _dropMenuRef = useRef<HTMLDivElement>(null);

	@observable private _windowWidth: number = 0;
	@observable private _showDropList: boolean = false;

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get isAuthorized(): boolean {
		return this._userStore.isAuthorized;
	}

	get listRef() {
		return this._listRef;
	}

	get width() {
		return this._windowWidth;
	}

	get dropMenuRef() {
		return this._dropMenuRef;
	}

	get localeUrls() {
		return cflHeaderLinks[(this.i18n.languageOnly as keyof typeof cflHeaderLinks) ?? "en"];
	}

	get showDropList() {
		return this._showDropList;
	}

	dispose(): void {
		window.removeEventListener("resize", this.listenResize);
	}

	init(): void {
		this._windowWidth = window.innerWidth;
		window.addEventListener("resize", this.listenResize);
	}

	initLayout = () => {
		const DOTS_WIDTH = 110;
		if (!this._listRef.current || !this._windowWidth) {
			return;
		}

		const element = this._listRef.current;
		const list = Array.from(element.children);

		element.querySelectorAll(".hidden").forEach((el) => el.classList.remove("hidden"));
		list.forEach((el) => el.classList.remove("hidden", "reverse"));

		const clientWidth = element.clientWidth - DOTS_WIDTH; // width box with dot
		this._showDropList = element.scrollWidth > element.clientWidth;

		let sumWidth = 0;

		list.forEach((item, i) => {
			sumWidth += item.clientWidth;

			if (sumWidth >= clientWidth) {
				item.classList.add("hidden");
			} else {
				this._dropMenuRef.current?.children[i]?.classList.add("hidden");
			}
		});

		const lastVisibleChild = last(list.filter((el) => !el.classList.contains("hidden")));

		if (lastVisibleChild) {
			lastVisibleChild.classList.add("reverse");
		}
	};

	private listenResize = debounce(() => {
		this._windowWidth = window.innerWidth;
	}, 300);
}
